import React, { useMemo, useState, useEffect, useRef } from "react";

/* ======================= BRAND & CONSTANTS ======================= */
const BRAND = {
  company_name: "Fairway Digital Media",
  logo_url:
    "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/logos/Final%20Logo_small_280.png",
  brand_hex: "#31cb4b",
  text_hex: "#0b1a3c",
  muted_hex: "#6b7280",
  bg_hex: "#f7f8fa",
};
const PRIVACY_URL =
  "https://app.enzuzo.com/policies/privacy/40b534da-38c6-11f0-a33b-f768ae964d06";
const TODAY = new Date().toISOString().slice(0, 10);

/* ======================= PRODUCT PRESETS ======================== */
const PRODUCT_PRESETS = {
  appointments: {
    display_name: "Guaranteed Appointment Setting",
    product_description:
      "Deliver booked, qualified meetings with titles approved by client. Meetings are virtual, 30 minutes minimum, and must meet the agreed acceptance criteria (approved title, duration ≥30 minutes, required attendee present, no-show policy). Includes SDR outreach, scheduling, handoff details, and reporting.",
    pricing_blurb:
      "CPA is all-inclusive: (a) SDR base + booked-meeting commission; (b) dialing/VoIP + sequencing tech; (c) cell-number enrichment/validation; (d) Fairway PM & delivery. No hourly/platform fees.",
    ui_defaults: {
      unit_cost: "1000",
      timeline:
        "Program runs from {START} to {END}. Weekly roll-up includes attempts, connects, booked, held, accepted, and replacements pending.",
      deliverables: [
        "• Five completed meetings with five different prospects",
        "• Meeting definition: virtual, approved titles, ≥30 minutes",
        "• No-show policy: no-shows don’t count; one coordinated reschedule; repeat offenders → move on",
        "• Account universe: start with Top 100 target accounts",
        "• Training & playbooks: AI-assisted SDR enablement using public site assets + uploaded decks",
      ].join("\n"),
      highlights: [
        "• Payment Terms: Unit Cost: $1,000 per meeting. Prepaid (credit card accepted). Billable only if acceptance criteria are met; if a meeting fails acceptance within 2 business days, it’s replaced at no charge.",
        "• Term & Termination: Agreement runs for the program dates. Either party may terminate for breach with notice; Client owes for accepted meetings through termination.",
      ].join("\n"),
    },
  },
  lead_programs: {
    display_name: "Governed Lead Programs (MQL/HQL/BANT)",
    product_description:
      "Content-driven lead generation with supplier vetting, consent controls, validation, and routing. MQL/HQL/BANT definitions and acceptance criteria clarified in the SOW.",
    pricing_blurb:
      "CPL varies by ACV band, region, and seniority per Fairway rate card.",
  },
  data_enrichment: {
    display_name: "Fairway Data Solutions (Verification & Enrichment)",
    product_description:
      "Contact verification (email deliverability, phone type & status) with optional U.S. mobile/VoIP append. International verification available. Add-ons include LinkedIn URL and additional emails.",
    pricing_blurb:
      "Verification $0.80/record • Direct Dial Ready $1.00/record • Add-ons: LinkedIn URL +$0.04, extra emails +$0.12. TCPA/DNC flag included.",
  },
  digital_display: {
    display_name: "Fairway Digital Display (B2B Programmatic)",
    product_description:
      "Programmatic display built on licensed firmographic data (industry, size, role, seniority), premium inventory, brand-safety controls, and transparent measurement. Includes audience design, activation, optimization, and reporting.",
    pricing_blurb: "Media at $15–$20 CPM (scope dependent).",
  },
  website_visitors: {
    display_name: "Website Visitor Intelligence",
    product_description:
      "Identify companies visiting your website and map activity to actionable GTM plays. Includes implementation guidance and quarterly reporting cadence.",
    pricing_blurb:
      "$5,000 per quarter for sites under 10,000 monthly visitors.",
  },
  signals: {
    display_name: "GTM Intelligence & Intent Signals",
    product_description:
      "Account-level financial, strategy, and narrative signals for TAL design, scoring, and ABM prioritization. Includes workspace views and refresh cadence per tier.",
    pricing_blurb:
      "Tiers from $1,000–$5,000/month for up to 2,500 accounts. Add-ons: industry/region Deep Financial packs, custom segments, premium delivery. >5,000 accounts = custom pricing.",
  },
  abm_account_plans: {
    display_name: "ABM Account Plans (1:1 and 1:Few)",
    product_description:
      "Structured account plans for ABM motions. 1:1 plans are company-specific; 1:Few plans are segment-based (industry, size, or custom audience). Includes goals, buying-committee map, signals, plays, messaging, and next actions.",
    pricing_blurb:
      "1:1 plans $300 each • 1:Few plans $200 each (per segment plan).",
  },
  content: {
    display_name: "Content Creation & Strategic Messaging",
    product_description:
      "Client-facing assets (white papers, eBooks, case studies), internal enablement content, and SDR/AE sequences. Includes discovery, writing, design, and two rounds of revisions.",
    pricing_blurb:
      "Assets $375–$1,500 each; bundles of 3–5+ client-facing assets $5k–$20k. Internal enablement content typically ≈ one-third the client-facing price. Sequences/messaging from $2,500 up to $15k for multi-persona/campaign packages.",
  },
  llm_workshop: {
    display_name: "LLM Visibility & Content Modernization Workshop",
    product_description:
      "SEO-style, practical workshop on how LLMs interpret content and how to modernize site structure and ops. Includes readiness scorecard, schema/structure guidance, and prioritized action plan.",
    pricing_blurb: "Flat fee $10k–$15k depending on scope and participants.",
  },
};

/* ========================= HELPERS ========================= */
const BULLET_REGEX = /^\s*(?:[•\-–]|(?:\(?\d+[.)])|(?:[A-Za-z][.)]))\s+/;

const isBullet = (line) => BULLET_REGEX.test(line || "");
const stripBulletMarker = (line) =>
  (line || "")
    .replace(/^\s*[•\-–]\s*/, "")
    .replace(/^\s*\(?\d+[.)]\s*/, "")
    .replace(/^\s*[A-Za-z][.)]\s*/, "")
    .trim();

const escapeHtml = (s) =>
  (s || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

const renderListItems = (text) => {
  if (!text) return "";
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trimEnd())
    .filter(Boolean);
  return lines
    .filter((l) => isBullet(l))
    .map((l) => `<li>${escapeHtml(stripBulletMarker(l))}</li>`)
    .join("\n");
};

const renderScopeNotesBlock = (text) => {
  if (!text || !text.trim()) return "";
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trimEnd())
    .filter(Boolean);
  let i = 0;
  const intro = [];
  while (i < lines.length && !isBullet(lines[i])) {
    intro.push(lines[i]);
    i++;
  }
  const introHtml = intro.length
    ? `<p><strong>${escapeHtml(intro.join(" "))}</strong></p>`
    : "";
  const bullets = lines
    .slice(i)
    .map((l) =>
      isBullet(l)
        ? `<li>${escapeHtml(stripBulletMarker(l))}</li>`
        : `<li>${escapeHtml(l)}</li>`
    )
    .join("");
  const ulHtml = bullets ? `<ul>${bullets}</ul>` : "";
  return `<h3>Scope Notes</h3><div class="card">${introHtml}${ulHtml}</div>`;
};

const fmtPrice = (val) => {
  const v = (val || "").trim();
  if (!v || v.toLowerCase() === "tbd") return "TBD";
  if (v.startsWith("$")) return v;
  const n = Number(v.replace(/,/g, ""));
  return Number.isFinite(n)
    ? `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : v;
};

/* ========================= HTML BUILDER ========================= */
const buildHtml = ({ brand = BRAND, fields, preset, tcs }) => {
  const pricingExtra =
    (!fields.unit_cost || fields.unit_cost.trim().toLowerCase() === "tbd") &&
    preset.pricing_blurb
      ? `<li>${escapeHtml(preset.pricing_blurb)}</li>`
      : "";

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8"><title>SOW - ${escapeHtml(
    preset.display_name
  )} - ${escapeHtml(fields.client_name || "")}</title>
<meta name="viewport" content="width=device-width, initial-scale=1" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet">
<style>
:root{--brand:${brand.brand_hex};--brand10:rgba(49,203,75,.10);--ink:${
    brand.text_hex
  };--ink70:#0B1A3CB3;--muted:${brand.muted_hex};--line:#E5E7EB;--bg:${
    brand.bg_hex
  };--card:#FFFFFF;--radius-sm:8px;--radius-md:12px;--radius-lg:16px;--shadow-xs:0 1px 2px rgba(0,0,0,.06);--shadow-sm:0 2px 4px rgba(0,0,0,.08);--shadow-md:0 4px 14px rgba(0,0,0,.08);--HeadingXL:28px;--HeadingXL-lh:34px;--HeadingLG:22px;--HeadingLG-lh:28px;--Body:14px;--Body-lh:20px;--Label:12px;--Label-lh:16px}
*{box-sizing:border-box}html,body{height:100%}
body{font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:var(--ink);background:var(--bg);margin:0;font-size:var(--Body);line-height:var(--Body-lh);-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}
.wrap{max-width:960px;margin:32px auto 64px;background:var(--card);border-radius:var(--radius-lg);box-shadow:var(--shadow-md);overflow:hidden;border:1px solid var(--line)}
header.site{position:sticky;top:0;z-index:10;backdrop-filter:saturate(180%) blur(8px);background:linear-gradient(180deg,#fff 0%,#f6fbf8 100%);padding:16px 28px;display:flex;align-items:center;gap:16px;border-bottom:1px solid var(--line)}
@media print { header.site{ position:static; backdrop-filter:none } }
.logo-badge{display:inline-flex;align-items:center;justify-content:center;padding:8px 10px;background:#fff;border:1px solid var(--line);border-radius:12px;box-shadow:var(--shadow-xs)}
.logo-badge img{height:36px;display:block}
header.site h1{font-size:var(--HeadingXL);line-height:var(--HeadingXL-lh);margin:0;font-weight:700;color:var(--ink)}
.dek{color:var(--ink70);font-size:var(--Body);margin-top:2px}
.bar{height:2px;background:var(--brand)}
section{padding:28px} section+section{padding-top:0}
h2{font-size:var(--HeadingLG);line-height:var(--HeadingLG-lh);margin:0 0 10px;font-weight:700;letter-spacing:-.01em}
h3{font-size:16px;line-height:22px;margin:22px 0 8px;font-weight:600;color:var(--ink)}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:16px 24px}
@media (max-width:720px){ .grid{grid-template-columns:1fr} }
.k{color:var(--muted);font-size:var(--Label);line-height:var(--Label-lh);text-transform:uppercase;letter-spacing:.04em}
.card{background:var(--card);border:1px solid var(--line);border-radius:var(--radius-md);padding:18px;box-shadow:var(--shadow-sm);transition:box-shadow .15s ease-out,transform .15s ease-out;break-inside:avoid;page-break-inside:avoid}
.card:hover{box-shadow:var(--shadow-md)}
.pill{display:inline-block;background:var(--brand10);color:var(--brand);padding:4px 10px;border-radius:999px;font-size:12px;font-weight:600;vertical-align:middle;margin-left:8px}
.meta{display:flex;flex-direction:column;gap:6px}
ul{margin:8px 0 0 18px;padding:0;list-style:disc}
li{margin:6px 0;padding-left:2px}
footer{padding:16px 28px 28px;color:var(--muted);font-size:var(--Label)}
a{color:#0b5cff;text-decoration:none} a:hover{text-decoration:underline}
a:focus{outline:2px solid color-mix(in oklab,#0b5cff 40%, transparent);outline-offset:2px}
.note{color:var(--ink70)} .divider{height:1px;background:var(--line);margin:16px 0}
.page-break{page-break-before:always} .key-keep{break-inside:avoid;page-break-inside:avoid}
@media print{ .page-break{display:block} body{background:#fff} .wrap{box-shadow:none;border:none;margin:0;max-width:100%} a{color:inherit;text-decoration:underline} .card:hover{box-shadow:none} p{orphans:3;widows:3}}
.sign-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
@media (max-width:720px){ .sign-grid{grid-template-columns:1fr} }
.sign-line{display:block;border-bottom:1px solid var(--line);height:24px;margin:8px 0 16px}
.sign-label{font-size:var(--Label);color:var(--muted)}
</style>
</head>
<body>
<div class="wrap">
  <header class="site">
    <div class="logo-badge"><img src="${brand.logo_url}" alt="${escapeHtml(
    brand.company_name
  )} logo"/></div>
    <div><h1>Statement of Work</h1><div class="dek">Sales Agreement & Commercial Summary</div></div>
  </header>
  <div class="bar"></div>

  <section>
    <div class="grid">
      <div class="meta"><div class="k">Client</div><div><strong>${escapeHtml(
        fields.client_name
      )}</strong><br>${escapeHtml(fields.client_contact)}<br>${escapeHtml(
    fields.client_email
  )}</div></div>
      <div class="meta"><div class="k">Campaign</div><div><strong>${escapeHtml(
        fields.campaign_name
      )}</strong><br>${escapeHtml(fields.start_date)} → ${escapeHtml(
    fields.end_date
  )}</div></div>
      <div class="meta"><div class="k">Billing Address</div><div>${escapeHtml(
        fields.client_address
      ).replaceAll("\n", "<br>")}</div></div>
      <div class="meta"><div class="k">Prepared By</div><div><strong>${escapeHtml(
        brand.company_name
      )}</strong></div></div>
    </div>
  </section>

  <section>
    <h2>Solution Overview <span class="pill">${escapeHtml(
      preset.display_name
    )}</span></h2>
    <div class="card"><p>${escapeHtml(preset.product_description)}</p></div>

    <h3>Deliverables & Policies</h3>
    <div class="card"><ul>${renderListItems(
      fields.front_deliverables
    )}</ul></div>

    <h3>Timeline & Reporting</h3>
    <div class="card"><p>${escapeHtml(fields.front_timeline)
      .replace("{START}", escapeHtml(fields.start_date || "Start Date"))
      .replace("{END}", escapeHtml(fields.end_date || "End Date"))}</p></div>

    <h3>Commercials</h3>
    <div class="card">
      <ul>
        <li>Quantity: <strong>${Number(fields.quantity || 0)}</strong></li>
        <li>Unit Cost: <strong>${fmtPrice(fields.unit_cost)}</strong></li>
        <li>Total Cost: <strong>${fields.total_cost || "TBD"}</strong></li>
        ${pricingExtra}
      </ul>
      <div class="note">Pricing guidance appears when Unit Cost is left blank or set to “TBD”.</div>
    </div>

    ${renderScopeNotesBlock(fields.notes)}

    <h3>Key Highlights</h3>
    <div class="card key-keep"><ul>${renderListItems(
      fields.front_highlights
    )}</ul></div>

    <p class="k">This program is governed by the Sales Agreement located on the following pages.</p>
  </section>

  <div class="page-break"></div>
  <section>
    <h2>Sales Agreement — Terms & Conditions</h2>
    <div class="card">
      <h3>Authorization to Proceed</h3><p>${escapeHtml(tcs.authorization)}</p>
      <h3>Acceptance Criteria</h3><p>${escapeHtml(tcs.acceptance)}</p>
      <h3>Meeting Definition</h3><p>${escapeHtml(tcs.meeting_definition)}</p>
      <h3>No-Show Policy</h3><p>${escapeHtml(tcs.no_show_policy)}</p>
      <h3>Cancellation Window</h3><p>${escapeHtml(tcs.cancellation_window)}</p>
      <h3>People Swaps</h3><p>${escapeHtml(tcs.people_swaps)}</p>
      <h3>Payments</h3><p>${escapeHtml(tcs.payments)}</p><p>${escapeHtml(
    tcs.late_fees
  )}</p>
      <h3>Change Control</h3><p>${escapeHtml(tcs.change_control)}</p>
      <h3>Reporting & Evidence</h3><p>${escapeHtml(tcs.reporting)}</p>
      <h3>Data, Privacy & IP</h3><p>${escapeHtml(
        tcs.data_ip
      )}</p><p>${escapeHtml(tcs.privacy).replace(
    "{PRIVACY_URL}",
    "${PRIVACY_URL}"
  )}</p>
      <h3>Indemnification</h3><p>${escapeHtml(tcs.indemnity)}</p>
      <h3>Limitation of Liability</h3><p>${escapeHtml(tcs.limitation)}</p>
      <h3>Term & Termination</h3><p>${escapeHtml(tcs.termination)}</p>
      <h3>Electronic Business</h3><p>${escapeHtml(tcs.electronic_business)}</p>
      <div class="divider"></div>
      <p class="k">Privacy policy: <a href="${PRIVACY_URL}" target="_blank" rel="noopener">${PRIVACY_URL}</a></p>
    </div>
  </section>

  <section>
    <h2>Signatures</h2>
    <div class="sign-grid">
      <div class="card"><div class="k">Service Provider</div><p><strong>${escapeHtml(
        brand.company_name
      )}</strong></p><label class="sign-label">Name</label><span class="sign-line"></span><label class="sign-label">Title</label><span class="sign-line"></span><label class="sign-label">Date</label><span class="sign-line"></span></div>
      <div class="card"><div class="k">Client</div><p><strong>${escapeHtml(
        fields.client_name
      )}</strong></p><label class="sign-label">Name</label><span class="sign-line"></span><label class="sign-label">Title</label><span class="sign-line"></span><label class="sign-label">Date</label><span class="sign-line"></span></div>
    </div>
  </section>

  <footer>© ${new Date().getFullYear()} ${escapeHtml(
    brand.company_name
  )}. All rights reserved.</footer>
</div>
</body>
</html>`;
};

/* ============================ T&Cs ============================ */
const TCS = {
  authorization:
    "Client authorizes Fairway Digital Media (“Fairway”) to commence services described in this Statement of Work (SOW).",
  acceptance:
    "A meeting is accepted if all of the following are met: (i) attendee holds an approved title (or pre-approved equivalent), (ii) meeting lasts ≥ 30 minutes, (iii) the named contact attends (or an approved replacement per the swap policy), and (iv) the qualification checklist is completed in the notes/CRM prior to the invite. Otherwise, the meeting will be replaced at no charge.",
  meeting_definition:
    "Meetings are virtual by default and scheduled at least 24 hours in advance unless otherwise agreed.",
  no_show_policy:
    "Client or prospect no-shows do not count toward delivery. One coordinated reschedule is permitted; repeat no-shows may be bypassed to maintain pace.",
  cancellation_window:
    "Client cancellations within 24 hours of a scheduled meeting are counted as delivered. Cancellations earlier than 24 hours will be rescheduled once.",
  people_swaps:
    "Replacement with higher/equal seniority is acceptable if scope of responsibility is comparable and the replacement is briefed. Replacement with lower seniority requires prior Client approval or the meeting is non-billable.",
  payments:
    "Unless otherwise stated, fees are due in advance. A meeting is billable only if it meets the acceptance criteria and is not a no-show. If a delivered meeting fails acceptance within two (2) business days, Fairway will replace it at no charge.",
  late_fees:
    "Late payments may incur a service charge of 1.5% per month or the maximum allowed by law, whichever is less.",
  change_control:
    "Changes to target accounts, accepted titles, qualification criteria, or scope require written approval via change order and may affect timeline and cost.",
  reporting:
    "Weekly roll-up reporting includes: attempts, connects, booked, held, accepted, and replacements pending. Evidence and dashboard access per Appendix A.",
  data_ip:
    "All research, notes, and recordings generated for the program are shared with Client. Fairway retains ownership of process IP, tooling, and methods. Client data is processed solely to perform the services.",
  privacy:
    "Fairway processes data in accordance with its Privacy Policy ({PRIVACY_URL}). Services do not require consumer third-party cookies or non-consented consumer data.",
  indemnity:
    "Each party will defend and indemnify the other against third-party claims arising from its gross negligence or willful misconduct.",
  limitation:
    "Neither party is liable for indirect, incidental, consequential, special, exemplary, or punitive damages. Except for fees owed, total aggregate liability of either party is limited to fees paid under this SOW in the three (3) months preceding the claim.",
  termination:
    "Either party may terminate this SOW for material breach upon written notice if the breach is not cured within ten (10) business days. Upon termination, Client owes for accepted deliverables through the termination date.",
  electronic_business:
    "Electronic signatures and communications are valid and binding. Notices may be delivered electronically to the contacts designated in this SOW.",
};

/* ============================ APP ============================ */
export default function App() {
  /* --------- Form State --------- */
  const [clientName, setClientName] = useState("");
  const [clientContact, setClientContact] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [startDate, setStartDate] = useState(TODAY);
  const [endDate, setEndDate] = useState("");
  const presetKeys = Object.keys(PRODUCT_PRESETS);
  const [productKey, setProductKey] = useState(presetKeys[0]);
  const [quantity, setQuantity] = useState(1);
  const [unitCost, setUnitCost] = useState("");
  const [totalCost, setTotalCost] = useState("");
  const [notes, setNotes] = useState("");
  const [frontDeliverables, setFrontDeliverables] = useState(
    PRODUCT_PRESETS.appointments.ui_defaults.deliverables
  );
  const [frontTimeline, setFrontTimeline] = useState(
    PRODUCT_PRESETS.appointments.ui_defaults.timeline
  );
  const [frontHighlights, setFrontHighlights] = useState(
    PRODUCT_PRESETS.appointments.ui_defaults.highlights
  );

  /* --------- Preview (Blob URL) --------- */
  const [previewUrl, setPreviewUrl] = useState("");
  const lastUrl = useRef("");
  const preset = useMemo(() => PRODUCT_PRESETS[productKey], [productKey]);

  useEffect(() => {
    const n = Number(String(unitCost).replace(/[$,]/g, "")) || 0;
    const q = Number(quantity) || 0;
    setTotalCost(
      n
        ? `$${(n * q).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : ""
    );
  }, [quantity, unitCost]);

  // Apply ui_defaults when switching products
  useEffect(() => {
    const d = preset.ui_defaults || {};
    if (typeof d.unit_cost === "string") setUnitCost(d.unit_cost);
    if (typeof d.timeline === "string") setFrontTimeline(d.timeline);
    if (typeof d.deliverables === "string")
      setFrontDeliverables(d.deliverables);
    if (typeof d.highlights === "string") setFrontHighlights(d.highlights);
  }, [productKey]); // eslint-disable-line

  const buildFields = () => ({
    client_name: clientName,
    client_contact: clientContact,
    client_email: clientEmail,
    client_address: clientAddress,
    campaign_name: campaignName,
    start_date: startDate,
    end_date: endDate,
    quantity,
    unit_cost: unitCost,
    total_cost: totalCost,
    notes,
    front_deliverables: frontDeliverables,
    front_timeline: frontTimeline,
    front_highlights: frontHighlights,
  });

  const generateBlobUrl = (html) => {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    if (lastUrl.current) URL.revokeObjectURL(lastUrl.current);
    lastUrl.current = url;
    setPreviewUrl(url);
    return url;
  };

  const handleGenerate = () => {
    const html = buildHtml({
      brand: BRAND,
      fields: buildFields(),
      preset,
      tcs: TCS,
    });
    generateBlobUrl(html);
  };

  const handleOpenNewTab = () => {
    if (!previewUrl) return;
    window.open(previewUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadHtml = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    const safeClient = (clientName || "client").replace(/[^\w\-]+/g, "_");
    a.href = previewUrl;
    a.download = `SOW_${safeClient}_${preset.display_name.replace(
      /\s+/g,
      "_"
    )}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handlePrint = () => {
    if (!previewUrl) return;
    const w = window.open(previewUrl, "_blank", "noopener");
    if (!w) return;
    // Print when ready
    const onLoad = () => {
      w.focus();
      w.print();
      w.removeEventListener("load", onLoad);
    };
    w.addEventListener("load", onLoad);
  };

  /* ===================== Polished App Shell Styles ===================== */
  return (
    <div className="app-root">
      <style>{`
        :root{
          --brand:${BRAND.brand_hex}; --ink:${BRAND.text_hex};
          --muted:${BRAND.muted_hex}; --bg:${BRAND.bg_hex};
          --line:#E5E7EB;
        }
        *{box-sizing:border-box}
        body, #root, .app-root{min-height:100vh;background:var(--bg);color:var(--ink)}
        .container{max-width:1120px;margin:0 auto;padding:24px}
        .sticky-head{position:sticky;top:0;z-index:20;background:rgba(255,255,255,.85);
          backdrop-filter:saturate(180%) blur(10px);border-bottom:1px solid var(--line)}
        .logo-shell{border:1px solid var(--line);background:#fff;border-radius:12px;padding:6px 10px;box-shadow:0 1px 2px rgba(0,0,0,.06)}
        .h1{font-size:20px;font-weight:700;line-height:28px;margin:0}
        .dek{color:#475569;font-size:14px}
        .card{background:#fff;border:1px solid var(--line);border-radius:16px;box-shadow:0 4px 14px rgba(0,0,0,.08)}
        .section{padding:20px}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
        .grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px}
        @media (max-width:1000px){ .grid, .grid-3{grid-template-columns:1fr} }
        .label{font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:#64748b;margin:4px 0 6px}
        .input, .textarea, .select{width:100%;border:1px solid var(--line);border-radius:10px;padding:10px 12px;
          background:#fff; outline: none; transition: box-shadow .15s ease, transform .05s}
        .textarea{min-height:92px}
        .input:focus, .textarea:focus, .select:focus{box-shadow:0 0 0 3px rgba(49,203,75,.25);border-color:var(--brand)}
        .btn{border-radius:999px;padding:10px 14px;font-weight:600;transition:all .15s ease}
        .btn-primary{background:#0b5cff;color:white;box-shadow:0 2px 4px rgba(0,0,0,.10)}
        .btn-primary:hover{transform:translateY(-1px);box-shadow:0 8px 24px rgba(11,92,255,.25)}
        .btn-ghost{background:transparent;color:#0b5cff}
        .toolbar{display:flex;gap:10px;align-items:center;flex-wrap:wrap}
        .preview{border:1px solid var(--line);border-radius:12px;overflow:hidden}
        .preview iframe{width:100%;height:78vh;display:block;background:white}
        .hint{font-size:13px;color:#475569}
        .kicker{font-size:12px;color:#64748b;text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px}
        .preview-empty{padding:14px;color:#475569}
      `}</style>

      <header className="sticky-head">
        <div
          className="container"
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <div className="logo-shell">
            <img
              src={BRAND.logo_url}
              alt="Fairway logo"
              style={{ height: 26 }}
            />
          </div>
          <div>
            <div className="h1">Fairway SOW Builder</div>
            <div className="dek">
              Generate a polished, print-ready Statement of Work
            </div>
          </div>
        </div>
      </header>

      <main className="container" style={{ display: "grid", gap: 18 }}>
        {/* Client & Campaign */}
        <section className="card section">
          <div className="kicker">Client & Campaign</div>
          <div className="grid">
            <div>
              <label className="label">Client name</label>
              <input
                className="input"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Acme Corp"
              />
            </div>
            <div>
              <label className="label">Client contact</label>
              <input
                className="input"
                value={clientContact}
                onChange={(e) => setClientContact(e.target.value)}
                placeholder="Jane Doe, VP Marketing"
              />
            </div>
            <div>
              <label className="label">Client email</label>
              <input
                className="input"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="jane.doe@acme.com"
              />
            </div>
            <div>
              <label className="label">Billing address</label>
              <textarea
                className="textarea"
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
                placeholder="Street, City, State, Zip"
              />
            </div>
          </div>
        </section>

        {/* Offering & Commercials */}
        <section className="card section">
          <div className="kicker">Offering & Commercials</div>
          <div className="grid-3">
            <div>
              <label className="label">Campaign name</label>
              <input
                className="input"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Q1 ABM Pilot"
              />
            </div>
            <div>
              <label className="label">Start date</label>
              <input
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder={TODAY}
              />
            </div>
            <div>
              <label className="label">End date</label>
              <input
                className="input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="YYYY-MM-DD"
              />
            </div>
            <div>
              <label className="label">Offering</label>
              <select
                className="select"
                value={productKey}
                onChange={(e) => setProductKey(e.target.value)}
              >
                {Object.entries(PRODUCT_PRESETS).map(([k, p]) => (
                  <option key={k} value={k}>
                    {p.display_name}
                  </option>
                ))}
              </select>
              <div className="hint" style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  Preset Preview
                </div>
                <div>{PRODUCT_PRESETS[productKey].product_description}</div>
                {PRODUCT_PRESETS[productKey].pricing_blurb && (
                  <div style={{ color: "#64748b", marginTop: 4 }}>
                    Pricing: {PRODUCT_PRESETS[productKey].pricing_blurb}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="label">Qty</label>
              <input
                className="input"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Unit cost</label>
              <input
                className="input"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                placeholder="TBD or 1000"
              />
            </div>
            <div>
              <label className="label">Total</label>
              <input
                className="input"
                value={totalCost}
                onChange={(e) => setTotalCost(e.target.value)}
                placeholder=""
              />
            </div>
          </div>
        </section>

        {/* Front Page Summary */}
        <section className="card section">
          <div className="kicker">Front Page Summary</div>
          <div className="grid">
            <div>
              <label className="label">Deliverables (bullets)</label>
              <textarea
                className="textarea"
                value={frontDeliverables}
                onChange={(e) => setFrontDeliverables(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Timeline note</label>
              <textarea
                className="textarea"
                value={frontTimeline}
                onChange={(e) => setFrontTimeline(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Page-1 Highlights</label>
              <textarea
                className="textarea"
                value={frontHighlights}
                onChange={(e) => setFrontHighlights(e.target.value)}
              />
            </div>
            <div>
              <label className="label">
                Scope notes (intro line + bullets)
              </label>
              <textarea
                className="textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={`One-week "Go-Live" list\n a) …\n b) …`}
              />
            </div>
          </div>
        </section>

        {/* Actions */}
        <div className="toolbar">
          <button className="btn btn-primary" onClick={handleGenerate}>
            Generate HTML
          </button>
          <button
            className="btn btn-ghost"
            onClick={handleOpenNewTab}
            disabled={!previewUrl}
          >
            Open in New Tab
          </button>
          <button
            className="btn btn-ghost"
            onClick={handleDownloadHtml}
            disabled={!previewUrl}
          >
            Download HTML
          </button>
          <button
            className="btn btn-ghost"
            onClick={handlePrint}
            disabled={!previewUrl}
          >
            Print / Save PDF
          </button>
        </div>

        {/* Preview */}
        <section className="card section preview">
          <div className="kicker">Preview</div>
          {previewUrl ? (
            <iframe title="sow-preview" src={previewUrl} />
          ) : (
            <div className="preview-empty">
              Fill the form and click <strong>Generate HTML</strong> to preview
              your SOW.
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
